/**
 * useEntitlements — proactive feature gating hook.
 *
 * Fetches entitlements from the billing subscription endpoint and exposes
 * helpers to check feature availability before rendering UI or calling APIs.
 *
 * Usage:
 *   const { canAccess, planTier, isLoading } = useEntitlements();
 *   if (canAccess('ai_copilot')) { ... }
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import billingService from '../services/billingService';

/**
 * Features that are hidden entirely (not just gated) during beta launch.
 * These are incomplete or too fragile to expose to paying users.
 * Managed separately from plan-based entitlements.
 */
const LAUNCH_HIDDEN_FEATURES = new Set([
  // ── Incomplete / no backend ────────────────────────────────────────────────
  'webhook_management',     // webhooks stored in-memory only
  'social_login',           // OAuth buttons exist but no backend
  'discord_integration',    // not built
  'zapier_integration',     // not built
  'ip_allowlisting',        // DB-backed CRUD exists but enforcement not wired to middleware

  // ── Post-launch advanced features ─────────────────────────────────────────
  // Backend services are production-ready but these add surface area without
  // strengthening the core Connect→Monitor→Optimize→Report workflows.
  // Re-enable as gated beta features after launch stabilizes.
  'creative_scaling',              // Pro+ — audience/placement scaling recs
  'audience_analysis',             // Pro+ — segment performance bucketing
  'placement_insights',            // Pro+ — placement-level ROAS analysis
  'incrementality_experiments',    // Pro+ — control/test cell lift measurement
  'warehouse_export',              // Agency+ — Snowflake/BigQuery/Redshift export
  'cross_channel_optimization',    // Pro+ — automated spend rebalancing
  'competitive_intelligence',      // Pro+ — competitor ad tracking

  // ── Secondary features hidden for launch MVP ──────────────────────────────
  // Narrowing to 5 core workflows: connect → dashboard → audit → recommend → report.
  // These pages are functional but add cognitive load for new users.
  'automation_rules',              // Rule-based automation — hidden from nav
  'partner_program',               // Agency partner program
  'system_health',                 // System health diagnostics page
  'advanced_analytics',            // AI anomaly detection, weekly report
  'referral_program',              // Referral rewards program
]);

/**
 * Features labeled as "Beta" in the UI.  Functional but not yet hardened.
 * Kept to the AI features that appear on the core dashboard and are the
 * primary differentiator for the launch MVP.
 */
const BETA_FEATURES = new Set([
  'ai_copilot',
  'ai_insights',
  'ai_rule_suggestions',
]);

export function useEntitlements() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Demo accounts run fully unlocked so visitors can explore every feature.
  const isDemo = user?.is_demo === true;

  const { data, isLoading, error } = useQuery({
    queryKey: ['entitlements'],
    queryFn: billingService.getSubscription,
    staleTime: 60_000,          // 1 minute
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const entitlements = data?.entitlements ?? {};
  const usage = data?.usage ?? {};
  const limits = data?.limits ?? {};
  const planTier = data?.plan?.tier ?? 'free';
  const isActive = data?.is_active ?? false;
  const isTrial = data?.billing_status === 'trialing';
  const trialDaysRemaining = data?.trial_days_remaining ?? null;

  /**
   * Check if a feature is available on the current plan.
   * Returns false for launch-hidden features regardless of plan.
   */
  function canAccess(feature) {
    // Admin + demo bypass — both can access everything, including
    // launch-hidden features (the demo is a full product showcase).
    if (isAdmin || isDemo) return true;
    if (LAUNCH_HIDDEN_FEATURES.has(feature)) return false;
    // If entitlements haven't loaded yet, fail closed
    if (!data) return false;
    // Check the entitlements map from the backend
    return !!entitlements[feature];
  }

  /**
   * Check if a feature should show a "Beta" badge.
   */
  function isBeta(feature) {
    return BETA_FEATURES.has(feature);
  }

  /**
   * Check if a feature is hidden at the launch level (not just plan-gated).
   */
  function isHidden(feature) {
    return LAUNCH_HIDDEN_FEATURES.has(feature);
  }

  /**
   * Check if a usage limit has been reached.
   * @param {string} resource - e.g. 'campaigns', 'connected_accounts', 'automation_rules'
   */
  function isAtLimit(resource) {
    if (isAdmin || isDemo) return false;
    const u = usage[resource];
    if (!u) return false;
    if (u.limit === -1) return false; // unlimited
    return u.used >= u.limit;
  }

  /**
   * Get usage info for a resource.
   */
  function getUsage(resource) {
    return usage[resource] ?? { used: 0, limit: 0 };
  }

  return {
    // State
    isLoading,
    error,
    planTier,
    isActive,
    isTrial,
    trialDaysRemaining,
    entitlements,
    usage,
    limits,

    // Helpers
    canAccess,
    isBeta,
    isHidden,
    isAtLimit,
    getUsage,
  };
}

export { LAUNCH_HIDDEN_FEATURES, BETA_FEATURES };
